// UI presentation layer over the Lib schema (algorithms.ts). This is chrome,
// not contract: Spanish labels/help for each algorithm, filter, and parameter,
// plus the one thing the Lib schema can't tell us — which `int` params are
// column selectors and which model (1|2) they bind to. Column params reference
// a POSITION within that model's columns[] (SPEC/1489 §"Convención de índices"),
// so the form renders them as a dropdown over the model's declared columns.
//
// A drift-guard test (catalogOverlay.test.ts) asserts every schema param has an
// entry here and vice versa — add a Lib param, this file must grow to match.

import type { ColumnKind, ColumnRole } from './columnRoles';

export interface ParamOverlay {
  label: string;
  help?: string;
  /** Present ⇒ render as a column selector (position into a model's columns[]),
   *  and this is what the param accepts. `kind` is enforced (a grade param can't
   *  take a text column); `role` only decides which names are suggested first —
   *  see config/columnRoles.ts. */
  column?: ColumnSpec;
  /** For suffixed algorithm params: which model's columns. Filter column
   *  params leave this undefined — they bind to the model the filter is
   *  assigned to via model_filters. */
  model?: 1 | 2;
}

export interface ColumnSpec {
  kind: ColumnKind;
  role?: ColumnRole;
}

export interface AlgorithmOverlay {
  label: string;
  description: string;
  /**
   * Name to use where the discriminant is stated right after it (see
   * `chartIdentity`). Only for labels that NAME that discriminant themselves:
   * "Mapa de acierto según ley de corte · corte 0.5" said "ley de corte" twice, and
   * the words spent on the repetition are the ones the identity needs. The tree
   * nodes and the gallery keep the full `label` — there the analysis has to be
   * distinguishable from its siblings with nothing else on the line.
   */
  shortLabel?: string;
}

export interface FilterOverlay {
  label: string;
  description: string;
}

export const ALGORITHM_OVERLAY: Record<string, AlgorithmOverlay> = {
  // Both Lib names share ONE unified label so the selected-row header stays
  // stable when the card switches between them (by cut-off vs by category).
  tonnage_grade_metal_by_cutoff: {
    label: 'Tonelaje - Ley - Metal',
    description: 'Compara tonelaje, ley y metal entre modelos por ley(es) de corte y, opcionalmente, por categoría.',
  },
  tonnage_grade_metal_by_categories: {
    label: 'Tonelaje - Ley - Metal',
    description: 'Compara tonelaje, ley y metal entre modelos por ley(es) de corte y, opcionalmente, por categoría.',
  },
  histogram: {
    label: 'Histograma',
    description: 'Distribución comparada de leyes entre dos modelos (LP vs CP).',
  },
  scatter: {
    label: 'Gráfico de dispersión',
    description: 'Dispersión con clasificación 1–4 y regresión lineal.',
  },
  error: {
    label: 'Error Tonelaje - Ley - Metal',
    description: 'Error (absoluto/relativo, acumulativo) entre dos modelos.',
  },
  map_accuracy_cutoff: {
    label: 'Mapa de acierto según ley de corte',
    shortLabel: 'Mapa de acierto',
    description: 'Matriz de exactitud sobre una ley de corte.',
  },
  map_accuracy_category: {
    label: 'Mapa de acierto por Categoría',
    shortLabel: 'Mapa de acierto',
    description: 'Matriz de exactitud sobre una categoría.',
  },
  // A bar chart (one bar per category), not a plan-view map — Bug 2732.
  match_geology: {
    label: 'Gráfico de acierto según Geología',
    description: 'Concordancia de categorías geológicas entre dos modelos.',
  },
  accuracy_cutoff: {
    label: 'Porcentaje de acierto según ley de corte',
    shortLabel: 'Porcentaje de acierto',
    description: 'Exactitud sobre una lista de leyes de corte.',
  },
};

// Labels an algorithm used to have. A chart's figure name is minted from the
// label when the chart is added (`mintFigureTitle`: "<label>" or "<label> #N")
// and saved with the reconciliation, so a chart created before a rename keeps
// the old default until it's brought forward on read (Bug 2732).
const PREVIOUS_DEFAULT_LABELS: Record<string, string> = {
  match_geology: 'Mapa de acierto según Geología',
};

/**
 * `title` with an algorithm's previous default label swapped for its current one.
 * Only the untouched default ("<old label>" or "<old label> #N") is renamed — a
 * name the user typed is theirs. Kept as is when the renamed name is already in
 * `taken`, so a reconciliation never ends up with two figures of the same name.
 */
export function currentFigureTitle(
  algorithm: string,
  title: string,
  taken: Iterable<string> = []
): string {
  const previous = PREVIOUS_DEFAULT_LABELS[algorithm];
  const label = ALGORITHM_OVERLAY[algorithm]?.label;
  if (!previous || !label) return title;
  const match = title === previous || /^ #\d+$/.test(title.slice(previous.length));
  if (!title.startsWith(previous) || !match) return title;
  const renamed = label + title.slice(previous.length);
  return new Set(taken).has(renamed) ? title : renamed;
}

export const FILTER_OVERLAY: Record<string, FilterOverlay> = {
  spatial: { label: 'Espacial', description: 'Filtra por sólidos (archivos STL/DXF).' },
  combined: { label: 'Combinado', description: 'Expresión booleana sobre otros filtros.' },
  categorical: { label: 'Categórico', description: 'Filtra por valor de una columna categórica.' },
  continuous: { label: 'Continuo', description: 'Filtra por rango de una columna continua.' },
  true: { label: 'Pasar todo', description: 'Sin filtro (deja pasar todos los bloques).' },
  false: { label: 'Bloquear todo', description: 'Rechaza todos los bloques.' },
};

// The three column shapes the algorithms ask for. GRADE also covers "value" and
// "variable to compare" params: in practice both are assay variables, so the
// grade names are the right ones to surface first.
const GRADE = { kind: 'numeric', role: 'grade' } as const;
const DENSITY = { kind: 'numeric', role: 'density' } as const;
const CATEGORY = { kind: 'category', role: 'category' } as const;

const col = (label: string, model: 1 | 2, column: ColumnSpec, help?: string): ParamOverlay => ({
  label,
  model,
  column,
  help,
});

export const PARAM_OVERLAY: Record<string, ParamOverlay> = {
  // --- column selectors (algorithm params, model-bound by _1/_2 suffix) ---
  grade_columns_index_1: col('Columna de ley (LP)', 1, GRADE),
  grade_columns_index_2: col('Columna de ley (CP)', 2, GRADE),
  density_column_index_1: col('Columna de densidad (LP)', 1, DENSITY),
  density_column_index_2: col('Columna de densidad (CP)', 2, DENSITY),
  category_column_index_1: col('Columna de categoría (LP)', 1, CATEGORY),
  category_column_index_2: col('Columna de categoría (CP)', 2, CATEGORY),
  variable_comparison_column_1: col('Columna a comparar (LP)', 1, GRADE),
  variable_comparison_column_2: col('Columna a comparar (CP)', 2, GRADE),
  category_column_1: col('Columna de categoría (LP)', 1, CATEGORY),
  category_column_2: col('Columna de categoría (CP)', 2, CATEGORY),
  value_columns_1: col('Columna de ley (LP)', 1, GRADE),
  value_columns_2: col('Columna de ley (CP)', 2, GRADE),
  density_columns_1: col('Columna de densidad (LP)', 1, DENSITY),
  density_columns_2: col('Columna de densidad (CP)', 2, DENSITY),
  category_comparison_column_1: col(
    'Columna categórica a comparar (LP)',
    1,
    CATEGORY,
    'Atributo categórico cuya coincidencia entre modelos (LP vs CP) define el acierto.'
  ),
  category_comparison_column_2: col(
    'Columna categórica a comparar (CP)',
    2,
    CATEGORY,
    'Atributo categórico cuya coincidencia entre modelos (LP vs CP) define el acierto.'
  ),

  // --- non-column algorithm params ---
  use_global_density: { label: 'Usar densidad global' },
  global_density_value: { label: 'Valor de densidad global' },
  global_density: { label: 'Densidad global' },
  volume: { label: 'Volumen de bloque' },
  return_m_tonnage: { label: 'Tonelaje en millones (Mt)' },
  by_cutoffs: { label: 'Agrupar por cutoffs' },
  cutoffs: { label: 'Leyes de corte', help: 'Lista de valores de corte.' },
  cutoff: { label: 'Ley de corte' },
  unique_categories: { label: 'Categorías', help: 'Lista de categorías a considerar.' },
  unique_cutOff: { label: 'Leyes de corte', help: 'Lista de valores de corte.' },
  // HACK(lib-2165): withdrawn from the form — see PARAM_VISIBILITY.error below and
  // the forcing in batchRequestBuilder. The entry stays because the overlay
  // drift-guards require one per schema param (same as `volume` and `by_cutoffs`),
  // and because it is what gets restored when the bug lands.
  //
  // AlgoritmoError::finalizarProceso: tonnage and metal push `previous + current`
  // from the SECOND category on (the first is pushed as-is), so those two series
  // are running totals. The grade series is NOT accumulated: it is recomputed as
  // `100 * metalAcc / tonnageAcc`, the quotient of those two accumulated ERRORS,
  // which is neither an error nor a grade — Bug 2165. The previous Lib (945fe95f)
  // accumulated numerator and denominator separately, which was right; the rewrite
  // lost it. Drawn on a "%" axis that quotient reads as a deviation: with tonnage
  // errors that nearly cancel it explodes (a real trace printed 137.5 where the
  // deviation was ~2). `preScaledWhen` in chartIntent still exempts it from the
  // ×100 for results ALREADY executed this way.
  is_accumulative_error: {
    label: 'Error acumulativo',
    help: 'Tonelaje y metal muestran el acumulado de las categorías anteriores; la ley no se acumula, se recalcula como cociente de esos dos errores acumulados.',
  },
  category_check: { label: 'Categoría a verificar' },

  // --- filter params (column ones bind to the assigned model) ---
  solids: { label: 'Sólidos', help: 'Archivos de sólidos (STL/DXF) desde storage-hub.' },
  expression: { label: 'Expresión', help: 'Ej: "fA AND NOT fB" sobre ids de filtros.' },
  category_column: { label: 'Columna categórica', column: CATEGORY },
  category_value: { label: 'Valor' },
  // No role: a continuous filter legitimately cuts on any numeric column (an
  // elevation, a grade, a tonnage), so nothing gets suggested over the rest.
  continuous_column: { label: 'Columna continua', column: { kind: 'numeric' } },
  min_limit_value: { label: 'Mínimo' },
  max_limit_value: { label: 'Máximo' },
};

export function getParamOverlay(name: string): ParamOverlay {
  return PARAM_OVERLAY[name] ?? { label: name };
}

// Per-algorithm label overrides, for a param whose shared label would read wrong
// in one algorithm's form. match_geology (Bug 2732) renders `category_column_*`
// and `category_comparison_column_*` as two rows; the shared "Columna categórica
// a comparar" (map_accuracy_category's wording) next to "Columna de categoría"
// doesn't say the second one is compared against the first.
const PARAM_LABEL_BY_ALGORITHM: Record<string, Record<string, string>> = {
  match_geology: {
    category_comparison_column_1: 'Columna de categoría a comparar (LP)',
    category_comparison_column_2: 'Columna de categoría a comparar (CP)',
  },
};

/** A param's label as `algorithm`'s form shows it: its override, else the overlay's. */
export function getParamLabel(algorithm: string, name: string): string {
  return PARAM_LABEL_BY_ALGORITHM[algorithm]?.[name] ?? getParamOverlay(name).label;
}

// Conditional visibility: some params are driven by another param or are derived
// elsewhere, so they're hidden from the form. Hiding is safe — the value persists
// in `parameters` (seeded by defaultParameters), so the wrapper still receives it
// and parse_parameters passes. Rules are algorithm-specific.
type VisibleWhen = (params: Record<string, unknown>) => boolean;

const hidden: VisibleWhen = () => false;

// Shared tonnage rules: density toggle drives value-vs-column; by_cutoffs is fixed
// by the chosen algorithm; volume is derived from the block size at build time.
const tonnageRules: Record<string, VisibleWhen> = {
  by_cutoffs: hidden,
  volume: hidden,
  global_density_value: (p) => p.use_global_density === true,
  density_column_index_1: (p) => p.use_global_density === false,
  density_column_index_2: (p) => p.use_global_density === false,
};

/**
 * Whether an `error` algorithm is in global-density mode: the `use_global_density`
 * bool the density toggle writes (SPEC/2597 — no value is needed, the constant
 * cancels out of the percent errors). A config saved before that has no flag and
 * picked the mode by the PRESENCE of `global_density`; nothing normalizes a
 * persisted config on load, so that still counts. buildBatchRequest lowers both
 * forms to the flag.
 */
export const errorUsesGlobalDensity = (p: Record<string, unknown>): boolean =>
  typeof p.use_global_density === 'boolean'
    ? p.use_global_density
    : 'global_density' in p;

// `error`'s density toggle is rendered by AlgorithmCard (it stashes the density
// columns and shows a note instead of a value), so neither the flag nor the value
// shows as its own field, and the density columns only show while it is off.
const usesDensityColumns: VisibleWhen = (p) => !errorUsesGlobalDensity(p);

const PARAM_VISIBILITY: Record<string, Record<string, VisibleWhen>> = {
  tonnage_grade_metal_by_cutoff: {
    ...tonnageRules,
    // Category columns are unused by the Lib here (by_cutoffs is forced true), so
    // normally hidden — BUT the unified "por categoría y ley de corte" mode
    // (scenario 3) keeps a `unique_categories` split list on a by_cutoff entry and
    // needs the column pickers to define the split dimension.
    category_column_index_1: (p) => p.by_cutoffs === false || 'unique_categories' in p,
    category_column_index_2: (p) => p.by_cutoffs === false || 'unique_categories' in p,
  },
  tonnage_grade_metal_by_categories: { ...tonnageRules },
  error: {
    volume: hidden,
    // HACK(lib-2165): the accumulative mode's grade series is a quotient of
    // accumulated errors, so the figure would print a fabricated percentage (see
    // PARAM_OVERLAY.is_accumulative_error). Hiding it is only half the withdrawal —
    // a config SAVED with `true` keeps the value, which is why buildBatchRequest
    // forces it off. Delete both when the bug lands.
    is_accumulative_error: hidden,
    use_global_density: hidden,
    global_density: hidden,
    density_columns_1: usesDensityColumns,
    density_columns_2: usesDensityColumns,
  },
};

export function isParamVisible(
  algorithm: string,
  param: string,
  params: Record<string, unknown>
): boolean {
  const rule = PARAM_VISIBILITY[algorithm]?.[param];
  return rule ? rule(params) : true;
}

// Display order: render the visible params in a flow that reads well (e.g. the
// density toggle before the density inputs). Params not listed keep schema order,
// appended after the listed ones.
const PARAM_ORDER: Record<string, string[]> = {
  // Options render in array order (column params drop into the matrix instead):
  // cut-offs right after the plotted variables, then the density toggle and the
  // "tonnage in Mt" flag last. use_global_density stays ahead of the density
  // column inputs so the toggle reads before the columns it governs.
  tonnage_grade_metal_by_cutoff: [
    'grade_columns_index_1',
    'grade_columns_index_2',
    'cutoffs',
    'use_global_density',
    'global_density_value',
    'density_column_index_1',
    'density_column_index_2',
    'return_m_tonnage',
    'category_column_index_1',
    'category_column_index_2',
  ],
  tonnage_grade_metal_by_categories: [
    'grade_columns_index_1',
    'grade_columns_index_2',
    'cutoff',
    'unique_categories',
    'use_global_density',
    'global_density_value',
    'density_column_index_1',
    'density_column_index_2',
    'return_m_tonnage',
    'category_column_index_1',
    'category_column_index_2',
  ],
  // The schema lists category columns before the value (ley) columns and puts
  // `unique_categories` first; flip that so the matrix reads ley, then category,
  // then its values (the order users fill them in), with density last.
  // `is_accumulative_error` is listed for when it comes back — it is hidden today
  // (HACK(lib-2165)), and orderParams only sorts what the form already shows.
  error: [
    'is_accumulative_error',
    'value_columns_1',
    'value_columns_2',
    'category_column_1',
    'category_column_2',
    'unique_categories',
    'global_density',
    'density_columns_1',
    'density_columns_2',
  ],
};

export function orderParams<T extends { name: string }>(algorithm: string, specs: T[]): T[] {
  const order = PARAM_ORDER[algorithm];
  if (!order) return specs;
  const idx = (n: string) => {
    const i = order.indexOf(n);
    return i === -1 ? order.length : i;
  };
  return [...specs].sort((a, b) => idx(a.name) - idx(b.name));
}
